import { useState } from "react";
import {
  FaMoneyBillWave,
  FaCalendarAlt,
  FaClock,
  FaGift,
} from "react-icons/fa";

type BonusType = "percentage" | "fixed" | "custom";
type AppliesTo = "all" | "selected";

export default function PayrollSettings() {
  const [paymentDate, setPaymentDate] = useState("");

  const [defaultPayroll, setDefaultPayroll] = useState<
    "current" | "previous"
  >("previous");

  const [bonusEnabled, setBonusEnabled] = useState(false);

  const [bonusType, setBonusType] = useState<BonusType>("percentage");

  const [bonusValue, setBonusValue] = useState("");

  const [bonusName, setBonusName] = useState("");

  const [bonusDescription, setBonusDescription] = useState("");

  const [bonusAppliesTo, setBonusAppliesTo] =
    useState<AppliesTo>("all");

  const handleSave = () => {
    const settings = {
      paymentDate,
      defaultPayroll,
      bonus: {
        enabled: bonusEnabled,
        name: bonusName,
        description: bonusDescription,
        type: bonusType,
        value: bonusValue,
        appliesTo: bonusAppliesTo,
      },
    };

    console.log(settings);

    // TODO:
    // Send settings to your backend
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-indigo-50 text-indigo-600">
            {FaMoneyBillWave({})}
          </div>

          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              Payroll Settings
            </h1>

            <p className="mt-1 text-sm text-gray-500">
              Configure payroll periods, payment information and
              additional employee earnings.
            </p>
          </div>
        </div>
      </div>

      {/* Main Settings Card */}
      <div className="w-full rounded-2xl border border-gray-200 bg-white shadow-sm">
        {/* ========================================================= */}
        {/* PAYROLL PREFERENCES */}
        {/* ========================================================= */}

        <div className="border-b border-gray-100 px-5 py-4">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-indigo-50 text-indigo-600">
              {FaCalendarAlt({})}
            </div>

            <div>
              <h2 className="font-semibold text-gray-900">
                Payroll Preferences
              </h2>

              <p className="text-xs text-gray-500">
                Configure how payroll information is displayed.
              </p>
            </div>
          </div>
        </div>

        <div className="p-5">
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            {/* Default Payroll Period */}
            <div>
              <div className="mb-2 flex items-center gap-2">
                {FaClock({
                  className: "text-sm text-indigo-500",
                })}

                <label className="text-sm font-medium text-gray-700">
                  Default Payroll Period
                </label>
              </div>

              <select
                value={defaultPayroll}
                onChange={(e) =>
                  setDefaultPayroll(
                    e.target.value as "current" | "previous"
                  )
                }
                className="w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm text-gray-700 outline-none transition focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
              >
                <option value="current">Current Month</option>
                <option value="previous">Previous Month</option>
              </select>

              <p className="mt-2 text-xs text-gray-400">
                Determines which payroll period is selected
                automatically when the payroll page is opened.
              </p>
            </div>

            {/* Payment Date */}
            <div>
              <div className="mb-2 flex items-center gap-2">
                {FaCalendarAlt({
                  className: "text-sm text-indigo-500",
                })}

                <label className="text-sm font-medium text-gray-700">
                  Payment Date
                </label>
              </div>

              <input
                type="date"
                value={paymentDate}
                onChange={(e) => setPaymentDate(e.target.value)}
                className="w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm text-gray-700 outline-none transition focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
              />

              <p className="mt-2 text-xs text-gray-400">
                This date will appear as the payment date on
                generated payslips.
              </p>
            </div>
          </div>
        </div>

        {/* ========================================================= */}
        {/* BONUS / ADDITIONAL EARNINGS */}
        {/* ========================================================= */}

        <div className="border-t border-gray-100">
          {/* Section Header */}
          <div className="border-b border-gray-100 px-5 py-4">
            <div className="flex items-center gap-3">
              <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-emerald-50 text-emerald-600">
                {FaGift({})}
              </div>

              <div>
                <h2 className="font-semibold text-gray-900">
                  Bonus & Additional Earnings
                </h2>

                <p className="text-xs text-gray-500">
                  Configure bonuses or additional payments that can
                  be added to employee payroll.
                </p>
              </div>
            </div>
          </div>

          <div className="p-5">
            {/* Enable Bonus */}
            <div className="flex items-center justify-between rounded-xl border border-gray-200 bg-gray-50 p-4">
              <div>
                <p className="text-sm font-semibold text-gray-800">
                  Enable Bonus
                </p>

                <p className="mt-1 text-xs text-gray-500">
                  Allow bonuses or additional earnings to be included
                  in payroll.
                </p>
              </div>

              <button
                type="button"
                onClick={() => setBonusEnabled(!bonusEnabled)}
                className={`relative h-6 w-11 rounded-full transition ${
                  bonusEnabled
                    ? "bg-indigo-600"
                    : "bg-gray-300"
                }`}
                aria-label="Toggle bonus"
              >
                <span
                  className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow-sm transition ${
                    bonusEnabled
                      ? "left-[22px]"
                      : "left-0.5"
                  }`}
                />
              </button>
            </div>

            {/* Bonus Form */}
            {bonusEnabled && (
              <div className="mt-6 space-y-6">
                {/* Bonus Name & Description */}
                <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                  {/* Bonus Name */}
                  <div>
                    <label className="mb-2 block text-sm font-medium text-gray-700">
                      Bonus Name
                    </label>

                    <input
                      type="text"
                      value={bonusName}
                      onChange={(e) =>
                        setBonusName(e.target.value)
                      }
                      placeholder="e.g. Christmas Bonus"
                      className="w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm text-gray-700 outline-none transition focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
                    />

                    <p className="mt-2 text-xs text-gray-400">
                      This name will appear on the employee's
                      payslip.
                    </p>
                  </div>

                  {/* Description */}
                  <div>
                    <label className="mb-2 block text-sm font-medium text-gray-700">
                      Description
                    </label>

                    <input
                      type="text"
                      value={bonusDescription}
                      onChange={(e) =>
                        setBonusDescription(e.target.value)
                      }
                      placeholder="e.g. Year-end appreciation bonus"
                      className="w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm text-gray-700 outline-none transition focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
                    />

                    <p className="mt-2 text-xs text-gray-400">
                      Optional explanation for the bonus.
                    </p>
                  </div>
                </div>

                {/* Calculation */}
                <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                  {/* Calculation Type */}
                  <div>
                    <label className="mb-2 block text-sm font-medium text-gray-700">
                      Calculation Type
                    </label>

                    <select
                      value={bonusType}
                      onChange={(e) =>
                        setBonusType(
                          e.target.value as BonusType
                        )
                      }
                      className="w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm text-gray-700 outline-none transition focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
                    >
                      <option value="percentage">
                        Percentage of Salary
                      </option>

                      <option value="fixed">
                        Fixed Amount
                      </option>

                      <option value="custom">
                        Custom Amount per Employee
                      </option>
                    </select>

                    <p className="mt-2 text-xs text-gray-400">
                      Choose how the bonus amount should be
                      calculated.
                    </p>
                  </div>

                  {/* Value */}
                  {bonusType !== "custom" && (
                    <div>
                      <label className="mb-2 block text-sm font-medium text-gray-700">
                        {bonusType === "percentage"
                          ? "Percentage"
                          : "Amount"}
                      </label>

                      <div className="relative">
                        <span className="absolute left-4 top-1/2 -translate-y-1/2 text-sm text-gray-400">
                          {bonusType === "percentage"
                            ? "%"
                            : "₦"}
                        </span>

                        <input
                          type="number"
                          min="0"
                          value={bonusValue}
                          onChange={(e) =>
                            setBonusValue(e.target.value)
                          }
                          placeholder={
                            bonusType === "percentage"
                              ? "e.g. 10"
                              : "e.g. 50000"
                          }
                          className="w-full rounded-xl border border-gray-200 bg-white py-3 pl-9 pr-4 text-sm text-gray-700 outline-none transition focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
                        />
                      </div>

                      <p className="mt-2 text-xs text-gray-400">
                        {bonusType === "percentage"
                          ? "For example, 10% adds 10% of the employee's salary."
                          : "This amount will be added to each selected employee."}
                      </p>
                    </div>
                  )}
                </div>

                {/* Custom Amount */}
                {bonusType === "custom" && (
                  <div className="rounded-xl border border-indigo-100 bg-indigo-50 p-4">
                    <div className="flex gap-3">
                      <div className="mt-0.5 text-indigo-600">
                        {FaGift({})}
                      </div>

                      <div>
                        <p className="text-sm font-semibold text-indigo-900">
                          Custom amounts
                        </p>

                        <p className="mt-1 text-xs leading-5 text-indigo-700">
                          Different bonus amounts can be entered
                          for individual employees when processing
                          payroll.
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Applies To */}
                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700">
                    Applies To
                  </label>

                  <select
                    value={bonusAppliesTo}
                    onChange={(e) =>
                      setBonusAppliesTo(
                        e.target.value as AppliesTo
                      )
                    }
                    className="w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm text-gray-700 outline-none transition focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
                  >
                    <option value="all">
                      All Employees
                    </option>

                    <option value="selected">
                      Selected Employees
                    </option>
                  </select>

                  <p className="mt-2 text-xs text-gray-400">
                    Choose whether this bonus should apply to
                    everyone or only selected employees.
                  </p>
                </div>

                {/* Preview */}
                <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
                  <p className="text-xs font-medium uppercase tracking-wide text-gray-400">
                    Payslip Preview
                  </p>

                  <div className="mt-3">
                    <p className="text-sm font-semibold text-gray-800">
                      {bonusName || "Christmas Bonus"}
                    </p>

                    <p className="mt-1 text-xs text-gray-500">
                      {bonusDescription ||
                        "Year-end appreciation bonus"}
                    </p>

                    <p className="mt-3 text-sm font-semibold text-emerald-600">
                      {bonusType === "percentage"
                        ? `${bonusValue || "10"}% of salary`
                        : bonusType === "fixed"
                          ? `₦${Number(
                              bonusValue || 0
                            ).toLocaleString()}`
                          : "Custom amount per employee"}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* ========================================================= */}
        {/* SAVE */}
        {/* ========================================================= */}

        <div className="flex justify-end border-t border-gray-100 px-5 py-5">
          <button
            type="button"
            onClick={handleSave}
            className="rounded-xl bg-indigo-600 px-6 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-indigo-700 hover:shadow"
          >
            Save Settings
          </button>
        </div>
      </div>
    </div>
  );
}